import { type FC } from 'react';
import type { Payment, PaymentPayload, PaymentStatusObject } from '../../types';

interface PaymentsTabProps {
    payments: Payment[];
}

const PaymentsTab: FC<PaymentsTabProps> = ({ payments }) => {

    const getStatusBadgeColor = (status: string) => {
        const s = status?.toUpperCase();
        switch (s) {
            case 'SUCCESS':
            case 'COMPLETED':
            case 'PAYMENT_SUCCEEDED':
                return 'bg-emerald-50 text-emerald-700 ring-emerald-200';
            case 'PENDING':
                return 'bg-amber-50 text-amber-700 ring-amber-200';
            case 'FAILED':
            case 'DECLINED':
                return 'bg-rose-50 text-rose-700 ring-rose-200';
            default:
                return 'bg-stone-50 text-stone-700 ring-stone-200';
        }
    };

    if (!payments || payments.length === 0) {
        return (
            <div className="rounded-lg border border-dashed border-stone-200 p-12 text-center">
                <p className="text-sm text-stone-500 italic">No payment records found for this order.</p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <h3 className="text-sm font-semibold text-stone-900 uppercase tracking-wide">Transaction History</h3>
            <div className="rounded-lg border border-stone-200 bg-white overflow-hidden shadow-sm">
                <table className="w-full text-sm text-left">
                    <thead className="bg-stone-50 border-b border-stone-200">
                    <tr>
                        <th className="px-6 py-3 text-xs font-semibold text-stone-700 uppercase tracking-wide">Date</th>
                        <th className="px-6 py-3 text-xs font-semibold text-stone-700 uppercase tracking-wide">Method</th>
                        <th className="px-6 py-3 text-xs font-semibold text-stone-700 uppercase tracking-wide">Status</th>
                        <th className="px-6 py-3 text-xs font-semibold text-stone-700 uppercase tracking-wide">Details</th>
                        <th className="px-6 py-3 text-right text-xs font-semibold text-stone-700 uppercase tracking-wide">Amount</th>
                    </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-200">
                    {payments.map((payment) => {
                        // Parse payload if it's a string
                        const p = (
                            typeof payment.payload === 'string'
                                ? JSON.parse(payment.payload)
                                : payment.payload
                        ) as PaymentPayload | null;

                        // Detect wallet payment
                        const isWallet =
                            payment.payment_type === 'WALLET' ||
                            payment.payment_provider === 'wallet' ||
                            p?.covered_total === true;

                        // Extract raw amount
                        // For wallet payments, use order_total_cents from payload if payment.amount is 0
                        const rawValue = isWallet
                            ? (p?.order_total_cents ?? payment.amount ?? 0)
                            : (payment.amount || p?.amount || p?.amount_due_cents || 0);

                        const displayAmount = Number(rawValue) / 100;

                        // Determine status — wallet payments with covered_total are always succeeded
                        const statusName = typeof payment.status === 'object'
                            ? (payment.status as PaymentStatusObject)?.name
                            : payment.status;

                        const rawStatus = statusName
                            || (isWallet && p?.covered_total ? 'PAYMENT_SUCCEEDED' : null)
                            || (p?.resultDetails?.ExtendedDescription === 'Transaction Successful' ? 'SUCCESS' : 'PENDING');

                        const formattedStatus = rawStatus
                            .toLowerCase()
                            .replace(/_/g, ' ');

                        // Determine details text
                        const detailsText = isWallet
                            ? `Wallet credit applied: R${(Number(p?.wallet_applied_cents ?? 0) / 100).toFixed(2)}`
                            : p?.resultDetails?.ExtendedDescription
                            || p?.result?.description
                            || `ID: ${payment.checkout_id || 'N/A'}`;

                        return (
                            <tr key={payment.id} className="hover:bg-stone-50 transition-colors">
                                <td className="px-6 py-4 text-stone-600 whitespace-nowrap">
                                    {new Date(payment.created_at).toLocaleString('en-ZA', {
                                        day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                                    })}
                                </td>
                                <td className="px-6 py-4">
                                    <div className="flex flex-col">
                                        {isWallet ? (
                                            <span className="font-medium text-stone-900">Wallet</span>
                                        ) : (
                                            <>
                                                    <span className="font-medium text-stone-900 capitalize">
                                                        {p?.paymentBrand || payment.payment_provider || 'Card'}
                                                    </span>
                                                {p?.card?.last4Digits && (
                                                    <span className="text-[10px] text-stone-500 font-mono">
                                                            **** {p.card.last4Digits}
                                                        </span>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </td>
                                <td className="px-6 py-4">
                                        <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-bold ring-1 ring-inset shadow-sm capitalize ${getStatusBadgeColor(rawStatus)}`}>
                                            {formattedStatus}
                                        </span>
                                </td>
                                <td className="px-6 py-4 text-xs text-stone-500 max-w-xs">
                                    <p className="truncate" title={detailsText}>
                                        {detailsText}
                                    </p>
                                </td>
                                <td className="px-6 py-4 text-right font-semibold text-stone-900 whitespace-nowrap">
                                    R {displayAmount.toFixed(2)}
                                </td>
                            </tr>
                        );
                    })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default PaymentsTab;